/**
 * Process-lifetime cache of identification candidates, keyed by report id.
 *
 * The identification panels fetch `GET /reports/{id}/candidates` on mount.
 * That read is cheap-ish, but the component state lives only as long as the
 * component is mounted — so navigating away and back dropped the results,
 * re-showed the loading spinner, and restarted polling, making it look like
 * the (expensive) identification graph was re-running when it wasn't.
 *
 * This module-level map survives unmounts, so a returning panel can paint the
 * last-known candidates immediately and revalidate quietly in the background.
 * A manual refresh button forces a fresh read. It is intentionally simple
 * (no TTL / eviction): the app has no React-Query layer, the dataset is tiny,
 * and a full page reload clears it.
 */

import { SubjectWithCandidates } from '@/types/subject-candidate';

const cache = new Map<string, SubjectWithCandidates[]>();

/** Last-known candidates for a report, or `undefined` if never fetched. */
export function getCachedCandidates(
  reportId: string | null | undefined
): SubjectWithCandidates[] | undefined {
  if (!reportId) {
    return undefined;
  }
  return cache.get(reportId);
}

/** Write-through: store the latest candidates for a report. */
export function setCachedCandidates(
  reportId: string | null | undefined,
  candidates: SubjectWithCandidates[]
): void {
  if (!reportId) {
    return;
  }
  cache.set(reportId, candidates);
}

/** Drop a report's cached candidates (e.g. on hard refresh). */
export function clearCachedCandidates(reportId: string | null | undefined): void {
  if (!reportId) {
    return;
  }
  cache.delete(reportId);
}
